import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Card, CardContent, Button, Stack, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Alert, Paper,
  Tabs, Tab, Chip, Switch, FormControlLabel
} from '@mui/material';
import { Edit, Refresh } from '@mui/icons-material';
import { marketingAPI, gymsAPI } from '../services/api';
import PageHeader from '../components/PageHeader';

const CHANNELS = ['push', 'sms', 'whatsapp'];

export default function NotificationTemplates() {
  const currentUser = (() => { try { return JSON.parse(localStorage.getItem('user')); } catch { return null; } })();
  const gymId = currentUser?.gymId?._id || currentUser?.gymId;

  const [tab, setTab] = useState(0); // 0: push, 1: sms, 2: whatsapp
  const [templates, setTemplates] = useState({});
  const [gymNotifs, setGymNotifs] = useState({});
  const [loading, setLoading] = useState(true);
  
  // Dialog state
  const [editOpen, setEditOpen] = useState(false);
  const [editType, setEditType] = useState('marketing'); // 'marketing' or 'gym'
  const [editKey, setEditKey] = useState('');
  const [editChannel, setEditChannel] = useState('');
  const [editTitle, setEditTitle] = useState('');
  const [editBody, setEditBody] = useState('');
  const [editIsActive, setEditIsActive] = useState(true);
  const [editRule, setEditRule] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const [mktRes, gymRes] = await Promise.all([
        marketingAPI.getTemplates(),
        gymsAPI.getNotificationTemplates(gymId)
      ]);
      setTemplates(mktRes.data.data.templates);
      setGymNotifs(gymRes.data.data || {});
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (type, key, channel, template) => {
    setEditType(type);
    setEditKey(key);
    setEditChannel(channel);
    setEditTitle(template.title || '');
    setEditBody(template.body);
    setEditIsActive(template.isActive !== false);
    setEditRule(template.rule || {});
    setError('');
    setEditOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editBody.trim()) {
      setError('Template body cannot be empty');
      return;
    }
    setSaving(true);
    setError('');
    try {
      if (editType === 'gym') {
        const payload = {
          ...gymNotifs,
          [editKey]: {
            ...gymNotifs[editKey],
            title: editTitle,
            body: editBody,
          }
        };
        await gymsAPI.updateNotificationTemplates(gymId, payload);
      } else {
        await marketingAPI.updateTemplate({
          templateKey: editKey,
          channel: editChannel,
          body: editBody,
          isActive: editIsActive,
          rule: editRule,
        });
      }
      setEditOpen(false);
      loadTemplates();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async (key, channel) => {
    if (!window.confirm(`Reset this ${channel.toUpperCase()} template to default?`)) return;
    try {
      await marketingAPI.resetTemplate({ templateKey: key, channel });
      loadTemplates();
    } catch (err) {
      console.error('Reset failed:', err);
    }
  };

  const currentChannel = CHANNELS[tab];

  return (
    <Box>
      <PageHeader
        title="Notification Templates"
        subtitle="Manage automated message templates for Push Notifications, SMS, and WhatsApp."
        gradient="linear-gradient(90deg, #1D4ED8, #60A5FA)"
      />

      <Paper sx={{ mb: 3 }}>
        <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab label="Push Notifications" />
          <Tab label="SMS" />
          <Tab label="WhatsApp" />
        </Tabs>
      </Paper>

      <Stack spacing={3}>
        {loading ? <Typography>Loading templates...</Typography> : (
          <>
            {Object.keys(templates).map(key => {
              const templateData = templates[key][currentChannel];
              if (!templateData) return null; // In case push isn't initialized yet in DB

              return (
                <Card key={key} variant="outlined">
                  <CardContent>
                    <Typography variant="h6" fontWeight={600} sx={{ textTransform: 'capitalize', mb: 2 }}>
                      {key.replace('_', ' ')}
                    </Typography>
                    <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.paper' }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                        <Typography variant="subtitle2" sx={{ textTransform: 'uppercase', color: 'text.secondary' }}>
                          {currentChannel === 'push' ? 'Push Notification' : currentChannel}
                          {!templateData.isActive && (
                            <Chip size="small" label="Disabled" color="default" sx={{ ml: 1, height: 20 }} />
                          )}
                        </Typography>
                        <Stack direction="row" spacing={1}>
                          {templateData.isCustom && (
                            <Button size="small" color="error" startIcon={<Refresh />} onClick={() => handleReset(key, currentChannel)}>
                              Reset
                            </Button>
                          )}
                          <Button size="small" startIcon={<Edit />} onClick={() => handleEditClick('marketing', key, currentChannel, templateData)}>
                            Edit
                          </Button>
                        </Stack>
                      </Stack>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {templateData.body}
                      </Typography>
                    </Paper>
                  </CardContent>
                </Card>
              );
            })}
            
            {/* System Push Notifications */}
            {currentChannel === 'push' && Object.keys(gymNotifs).length > 0 && (
              <>
                <Typography variant="h6" sx={{ mt: 2 }}>System Notifications</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  These notifications are automatically triggered by system events and only support push notifications.
                </Typography>
                {Object.keys(gymNotifs).map(key => {
                  const templateData = gymNotifs[key];
                  return (
                    <Card key={key} variant="outlined">
                      <CardContent>
                        <Typography variant="h6" fontWeight={600} sx={{ textTransform: 'capitalize', mb: 2 }}>
                          {key.replace(/_/g, ' ')}
                        </Typography>
                        <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.paper' }}>
                          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                            <Typography variant="subtitle2" sx={{ textTransform: 'uppercase', color: 'text.secondary' }}>
                              Push Notification
                            </Typography>
                            <Button size="small" startIcon={<Edit />} onClick={() => handleEditClick('gym', key, 'push', templateData)}>
                              Edit
                            </Button>
                          </Stack>
                          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                            {templateData.title}
                          </Typography>
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                            {templateData.body}
                          </Typography>
                        </Paper>
                      </CardContent>
                    </Card>
                  );
                })}
              </>
            )}
          </>
        )}
      </Stack>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit {editChannel.toUpperCase()} Template - {editKey.replace('_', ' ')}</DialogTitle>
        <DialogContent dividers>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Alert severity="info" sx={{ mb: 2 }}>
            {editType === 'marketing' 
              ? `Available placeholders: {{memberName}}, {{gymName}}, {{expiryDate}}, {{daysLeft}}`
              : `Use placeholders carefully as they differ per notification event (e.g. {{amount}}, {{gymName}}, {{dueDate}})`
            }
          </Alert>
          {editType === 'gym' && (
            <TextField
              fullWidth
              variant="outlined"
              label="Notification Title"
              value={editTitle}
              onChange={e => setEditTitle(e.target.value)}
              sx={{ mb: 3 }}
            />
          )}
          <TextField
            fullWidth
            multiline
            rows={6}
            variant="outlined"
            label="Template Body"
            value={editBody}
            onChange={e => setEditBody(e.target.value)}
          />

          {editType === 'marketing' && (
            <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid #eee' }}>
              <Typography variant="subtitle2" gutterBottom>Automation Settings</Typography>
              <FormControlLabel
                control={
                  <Switch 
                    checked={editIsActive} 
                    onChange={(e) => setEditIsActive(e.target.checked)} 
                    color="primary"
                  />
                }
                label="Enable automated sending for this template"
              />
              {editKey === 'renewal_reminder' && editIsActive && (
                <Box sx={{ mt: 2 }}>
                  <TextField
                    label="Days before expiry"
                    type="number"
                    size="small"
                    value={editRule.daysBeforeExpiry ?? 3}
                    onChange={(e) => setEditRule({ ...editRule, daysBeforeExpiry: parseInt(e.target.value) || 0 })}
                    helperText="Send this message X days before the membership expires"
                    inputProps={{ min: 0, max: 30 }}
                  />
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveEdit} disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
